;(function() {
  'use strict'

  Factory.$inject = ["$timeout", "$location", "$rootScope", "glUtils", "ResponderQuestion", "LOI", "Logic", "LogicCondition", "LogicAction", "Video", "Pipes", "Lender", "Randomizer", "Timer", "locationSetService"];
  angular.module('app.core').factory('Responder', Factory)

  /* @ngInject */
  function Factory(
    $timeout,
    $location,
    $rootScope,
    glUtils,
    ResponderQuestion,
    LOI,
    Logic,
    LogicCondition,
    LogicAction,
    Video,
    Pipes,
    Lender,
    Randomizer,
    Timer,
    locationSetService
  ) {
    var QuestionTypes = ResponderQuestion.Types

    var CORE_PROFILE_KEYS = [
      'age_year_of_birth',
      'age_range',
      'sex',
      'location',
    ]

    var Dimensions = {
      DATA: 'DATA',
      REPORT: 'REPORT',
      SYSTEM: 'SYSTEM',
    }

    var Views = {
      INTRO: 'INTRO',
      SECTION: 'SECTION',
      QUESTION: 'QUESTION',
      PAGE: 'PAGE',
      OUTRO: 'OUTRO',
    }

    var Events = {
      START: 'responder:start',
      VIEW_CHANGED: 'responder:view-changed',
      VIEW_COMPLETED: 'responder:view-completed',
      SENT: 'responder:sent',
      SENT_ERROR: 'responder:sent-error',
      DONE: 'responder:done',
      AUTOCOMPLETED: 'responder:autocompleted',
    }

    var SectionMediaTypes = {
      VIDEO: 'VIDEO',
      VIDEO_UPLOAD: 'VIDEO_UPLOAD',
      AUDIO: 'AUDIO',
      IMAGE: 'IMAGE',
      IFRAME: 'IFRAME',
    }

    var Defaults = {
      isReadOnly: false,
      generatorAmount: 1,
      generatorParallel: 1,
    }

    /**
     * @class Responder
     * @desc instantiates a new responder for a survey
     * @param {Object} survey - The survey to respond to
     * @param {Boolean} isLoggedIn - Whether the user is logged in or not
     * @param {Boolean} isReadOnly - Whether the survey is readOnly or not
     */
    function Responder(
      survey,
      tagQuotas,
      tagCounts,
      isLoggedIn,
      isReadOnly,
      isKiosk,
      isPanel,
      isTemplate,
      deviceId,
      labels
    ) {
      var self = this
      this.id = survey.id
      this.isLoggedIn = isLoggedIn
      this.isReadOnly = isReadOnly
      this.isKiosk = isKiosk
      this.isPanel = isPanel
      this.isTemplate = isTemplate
      this.survey = _.cloneDeep(survey)
      this.outlink = _.get(survey, 'data.outlink', {})
      this.promotions = _.isPlainObject(this.survey.promotions)
        ? this.survey.promotions
        : {}
      this.questions = []
      this.views = []
      this.dimensions = []
      this.history = []
      this.headers = {}
      this.tagOps = []
      this.listEntries = []
      this.variableEntries = []

      this.hiddenViewGroupEntries = []
      this.viewGroupsById = {}
      _.each(self.survey.data.viewGroups, function(viewGroup) {
        self.viewGroupsById[viewGroup.id] = viewGroup
      })

      this.tagQuotas = tagQuotas || { complete: [], incomplete: [] }
      this.tagCounts = tagCounts || {}
      this.estimatedDuration = new LOI().deserialize(
        survey.lengthOfInterview
      ).respondentDurationLabel

      this.timer = new Timer()
      this.timer.start() // start timer immediately

      this.deviceId = deviceId
      this.instanceId = glUtils.uuid()
      this.submitAttempt = 0

      this.labels = labels
      this.aiQualityFilterEnabled = self.survey.data.aiQualityFilterEnabled
      this.preSubmits = [] // list of promises to resolve before submitting

      // Make choices/statements use a single ".label" field
      // instead of ".option" and ".statement"
      _.each(this.survey.questions, function(question) {
        _.each(question.choices, function(choice) {
          choice.label = choice.option || choice.statement
        })
        _.each(question.statements, function(statement) {
          statement.label = statement.option || statement.statement
        })
      })

      this.makeQuestions()
      this.makeViews()

      // once questions have been ordered and any randomizations applied,
      // we can now apply the "persist borrowed order" option
      this.questions.forEach(function(question) {
        question.applyPersistedBorrowedOrder()
      })

      this.next()

      _.defaultsDeep(this, _.cloneDeep(Defaults))
    }

    Responder.prototype.Dimensions = Responder.Dimensions = Dimensions
    Responder.prototype.Views = Responder.Views = Views
    Responder.prototype.Events = Responder.Events = Events
    Responder.prototype.SectionMediaTypes = Responder.SectionMediaTypes = SectionMediaTypes
    Responder.prototype.Defaults = Responder.Defaults = Defaults

    Responder.prototype.createResponderQuestion = function(
      questionRaw,
      number
    ) {
      return new ResponderQuestion().fromServer(questionRaw, {
        number: number,
        showNumber: this.survey.data.showQuestionNumbers,
        isLoggedIn: this.isLoggedIn,
        isTemplate: this.isTemplate,
        survey: this.survey,
        responder: this,
      })
    }

    /**
     * Make Questions
     * @desc remove hidden and create a collection of ResponderQuestions
     */
    Responder.prototype.makeQuestions = function() {
      var self = this

      // order questions
      var questions = _.orderBy(self.survey.questions, 'order')

      // turn into ResponderQuestions
      _.each(questions, function(question, index) {
        // ignore hidden
        if (question.isHidden) return

        // ignore followup on panel channels
        if (
          question.type === ResponderQuestion.Types.FOLLOWUP &&
          self.isPanel
        ) {
          return
        }

        var number = index + 1
        var responderQuestion = self.createResponderQuestion(question, number)
        self.questions.push(responderQuestion)
      })
    }

    Responder.prototype.createSectionView = function(sectionRaw) {
      var section = {
        id: sectionRaw.id,
        title: sectionRaw.title,
        titlePipes: new Pipes(sectionRaw.title),
        imageUrl: sectionRaw.imageUrl,
        imageFromLoop: sectionRaw.imageFromLoop,
        youTubeVideoUrl: sectionRaw.youTubeVideoUrl,
        video: new Video().deserialize(sectionRaw.video),
        audioUrl: sectionRaw.audioUrl,
        iframeUrl: sectionRaw.iframeUrl,
        logic: _.map(sectionRaw.logic, function(logic) {
          return new Logic().deserialize(logic).randomize()
        }),
        mediaType: sectionRaw.mediaType,
        delay: sectionRaw.delay,
        hide: sectionRaw.hide,
        timer: new Timer(),
      }
      return {
        type: Views.SECTION,
        section: section,
      }
    }

    Responder.prototype.createQuestionView = function(responderQuestion) {
      return {
        type: Views.QUESTION,
        question: responderQuestion,
        element: this.survey.element, // wtf is this for?
      }
    }

    /**
     * Make Views
     * @desc builds and populates the linear flow of views from the survey
     */
    Responder.prototype.makeViews = function() {
      var self = this
      self.viewsByQuestionId = {}
      self.viewsBySectionId = {}

      // set core-profile options
      self.coreProfile = {
        added: !!self.survey.data.coreProfileAdded,
        enabled: !!self.survey.data.coreProfileEnabled,
        isCompulsory: !!self.survey.data.coreProfileCompulsory,
        questions: [],
      }

      // add intro if enabled
      if (self.survey.showLandingPage) {
        self.views.push({
          type: Views.INTRO,
          intro: {
            title: self.survey.title,
            imageUrl: self.survey.imageUrl,
            info: self.survey.data.introText,
            infoUrl: self.survey.data.introTextUrl,
            timeVisible: self.survey.data.introTimeVisible,
            customDataVisible: self.survey.data.introCustomDataVisible,
          },
        })
      }

      self.survey.data.views.forEach(function(data) {
        var view
        if (data.type === Views.QUESTION) {
          var question = self.questions.find(function(question) {
            return question.id === data.value
          })
          if (!question) return // probably hidden
          view = self.createQuestionView(question)
          self.viewsByQuestionId[view.question.id] = view
        }
        if (data.type === Views.SECTION) {
          if (data.value.isHidden) return
          view = self.createSectionView(data.value)
          self.viewsBySectionId[view.section.id] = view
        }
        self.views.push(view)
      })

      // extract core-profile questions
      _.remove(self.views, function(view) {
        if (view.type !== Views.QUESTION) return
        var question = view.question
        var isCoreProfile = _.includes(
          CORE_PROFILE_KEYS,
          question.autocompleteKey
        )
        if (isCoreProfile) {
          self.coreProfile.questions.push(question)
          return true
        }
      })

      self.hiddenBySamplingViewIds = []

      // sample view groups
      _.each(self.survey.data.viewGroups, function(viewGroup) {
        if (!_.isNumber(viewGroup.sample) || viewGroup.sample < 1) return
        var shuffledViewIds = _.shuffle(viewGroup.viewIds)
        var hiddenBySamplingViewIds = _.slice(shuffledViewIds, viewGroup.sample)
        hiddenBySamplingViewIds.forEach(function(viewId) {
          self.hiddenBySamplingViewIds.push(viewId)
        })
      })

      // sample shuffle groups
      _.each(self.survey.data.shuffleGroups, function(shuffleGroup) {
        if (!_.isNumber(shuffleGroup.sample) || shuffleGroup.sample < 1) return
        var shuffledViewGroupIds = _.shuffle(shuffleGroup.viewGroupIds)
        var hiddenViewGroupIds = _.slice(
          shuffledViewGroupIds,
          shuffleGroup.sample
        )
        hiddenViewGroupIds.forEach(function(viewGroupId) {
          var viewGroup = _.find(self.survey.data.viewGroups, {
            id: viewGroupId,
          })
          viewGroup.viewIds.forEach(function(viewId) {
            self.hiddenBySamplingViewIds.push(viewId)
          })
        })
      })

      // shuffle groups
      _.each(self.survey.data.shuffleGroups, function(shuffleGroup) {
        // ignore when there is only 1 group
        if (shuffleGroup.viewGroupIds.length < 2) return // continue

        var viewsInGroups = []
        _.each(shuffleGroup.viewGroupIds, function(viewGroupId) {
          var viewGroup = _.find(self.survey.data.viewGroups, {
            id: viewGroupId,
          })
          if (!viewGroup) return // continue

          var startIndex = null
          var views = _.remove(self.views, function(view, index) {
            var viewId = null
            var shouldRemove = false
            if (view.type === Views.SECTION) viewId = view.section.id
            if (view.type === Views.QUESTION) viewId = view.question.id
            if (_.includes(viewGroup.viewIds, viewId)) {
              shouldRemove = true
            }
            if (shouldRemove && !_.isNumber(startIndex)) {
              startIndex = index
            }
            return shouldRemove
          })
          if (!views.length) return // continue

          // put back the first element of removed views as the pin point
          // position to be shuffled
          Array.prototype.splice.apply(
            self.views,
            [startIndex, 0].concat(views[0])
          )
          // collect the removed views in group to insert it back after shuffled
          viewsInGroups.push(views)
        })

        // get the start index from each group to use for shuffling
        var startIndexes = []
        var viewByStartIndex = {}
        _.each(viewsInGroups, function(views) {
          var startIndex = _.findIndex(self.views, views[0])
          startIndexes.push(startIndex)
          viewByStartIndex[startIndex] = views[0]
        })

        // shuffle the startIndexes and replace the original view position
        // with the new view position after shuffled
        var shuffled = _.shuffle(startIndexes)
        _.each(shuffled, function(startIndex, idx) {
          var originalStartIndex = startIndexes[idx]
          var view = viewByStartIndex[startIndex]
          Array.prototype.splice.apply(
            self.views,
            [originalStartIndex, 1].concat(view)
          )
        })

        // insert back the collected views from each group to the new position
        // after shuffled
        _.each(viewsInGroups, function(views) {
          var startIndex = _.findIndex(self.views, views[0])
          // if we had ES6 we could do:
          // self.views.splice(1, 1, ...views)
          // https://stackoverflow.com/questions/43018742/javascript-insert-array-in-another-array-at-a-specific-index
          Array.prototype.splice.apply(
            self.views,
            [startIndex, 1].concat(views)
          )
        })
      })

      // shuffle views within groups
      _.each(self.survey.data.viewGroups, function(viewGroup) {
        if (viewGroup.shuffleIds.length < 2) return // continue
        var startIndexes = []
        var viewByStartIndex = {}
        _.each(viewGroup.shuffleIds, function(viewId) {
          var startIndex = _.findIndex(self.views, function(view) {
            var id = null
            if (view.type === Views.SECTION) id = view.section.id
            if (view.type === Views.QUESTION) id = view.question.id
            return id === viewId
          })
          if (startIndex === -1) return // continue
          startIndexes.push(startIndex)
          viewByStartIndex[startIndex] = self.views[startIndex]
        })
        var shuffled = _.shuffle(startIndexes)
        _.each(shuffled, function(startIndex, idx) {
          var originalStartIndex = startIndexes[idx]
          var view = viewByStartIndex[startIndex]
          Array.prototype.splice.apply(
            self.views,
            [originalStartIndex, 1].concat(view)
          )
        })
      })

      // number sections + questions
      var sectionCursor = 0
      var questionCursor = 0
      _.each(self.views, function(view) {
        if (view.type === Views.SECTION) {
          view.section.number = ++sectionCursor
        }
        if (view.type === Views.QUESTION) {
          if (view.question.type === view.question.Types.HIDDEN_VARIABLES) {
            // skip numbering hidden variables question
            return
          }
          view.question.number = ++questionCursor
        }
      })

      // add core-profile page if enabled
      if (self.coreProfile.enabled) {
        self.views.push({
          type: Views.PAGE,
          questions: self.coreProfile.questions,
        })
      }

      // add outro
      self.views.push({
        type: Views.OUTRO,
        outro: {
          message: self.survey.data.outroText,
          restartButton: self.survey.data.restartButton,
          doneButton: self.survey.data.doneButton,
          isShown: false,
        },
      })

      // expand loops before we index views and count questions below
      self.expandLoops()

      // add view index reference
      _.each(self.views, function(view, index) {
        view.index = index
      })

      // count total questions
      self.totalQuestions = _.filter(this.views, {
        type: Views.QUESTION,
      }).length
    }

    Responder.prototype.expandLoops = function() {
      // viewGroups can be used as loops over a set of variables.
      // to simplify this in the respondent code we:
      // 1. expand/duplicate the group views for each variable
      // 2. replace {{ loop_variable }} with each variable label
      // 3. attach a piece of display logic to hide the view if
      //   respondent doesn't meet the criteria to see it (borrow-from rules)
      var self = this
      _.each(this.survey.data.viewGroups, function(viewGroup) {
        if (!viewGroup.loop) return

        var lender
        if (viewGroup.loopLender.id) {
          lender = self.views.find(function(view) {
            if (view.type !== Views.QUESTION) return false

            // find question using the ID or the loopCanonicalId if question have been expanded/cloned
            // using loopCanonicalId is not as accurate as ID because there could be multiple questions
            // with the same loopCanonicalId but this is acceptable because these cloned questions has the
            // same choices/statements to be used as loop variables. More validation for the borrow from rules
            // will be added as logic for each expanded views.
            var questionId = view.question.loopKey
              ? view.question.loopCanonicalId
              : view.question.id
            return questionId === viewGroup.loopLender.id
          })
        }

        // get all visible variables (local + borrowed)
        var variables = []
        var isVariableIdBorrowing = {} /* [variableId]: Boolean */
        if (lender) {
          var borrowedVariables = []
          if (viewGroup.loopLender.target === Lender.Target.STATEMENTS) {
            borrowedVariables = lender.question.statements.slice()
            borrowedVariables = lender.question.filterStatementsBySampling(
              borrowedVariables
            )
          }
          if (viewGroup.loopLender.target === Lender.Target.CHOICES) {
            borrowedVariables = lender.question.choices.slice()
            borrowedVariables = lender.question.filterChoicesBySampling(
              borrowedVariables
            )
          }
          borrowedVariables.forEach(function(variable) {
            variables.push(variable)
            isVariableIdBorrowing[variable.id] = true
          })
        }
        viewGroup.loopVariables.forEach(function(variable) {
          if (variable.data.isHidden) return // skip hidden variables
          variables.push(variable)
          isVariableIdBorrowing[variable.id] = false
        })

        // store a copy of all visible variables in an attribute
        // so it can be used by logic-debugger
        viewGroup.allVisibleVariables = variables.slice()

        // apply randomization.
        function getIsAnchored(variable) {
          return variable.data.isAnchored
        }
        var randomizer = new Randomizer()
        if (viewGroup.loopRandomize === 'SHUFFLE') {
          randomizer.shuffle(variables, getIsAnchored)
        }
        if (viewGroup.loopRandomize === 'ROTATE') {
          randomizer.rotate(variables, getIsAnchored)
        }
        if (viewGroup.loopRandomize === 'FLIP') {
          randomizer.flip(variables, getIsAnchored)
        }

        // // debug
        // console.log(
        //   'borrowed variables',
        //   _.map(borrowedVariables, function(v) {
        //     return v.option || v.statement
        //   }).join(', ')
        // )
        // console.log(
        //   'local variables',
        //   _.map(localVariables, function(v) {
        //     return v.option || v.statement
        //   }).join(', ')
        // )
        // console.log('borrowed variables:', borrowedVariables.length)
        // console.log('local variables:', localVariables.length)

        // find the start index for the group, and
        // remove the viewGroup views
        var startIdx = self.views.findIndex(function(view) {
          var id = self.getViewId(view)
          return viewGroup.viewIds.includes(id)
        })
        var views = _.remove(self.views, function(view) {
          var id = self.getViewId(view)
          return viewGroup.viewIds.includes(id)
        })

        function inject(string, variable) {
          return string
            ? string.replace(/{{[\s]*loop_variable[\s]*}}/gi, variable)
            : string
        }

        // duplicate all views in the group, once for each
        // variable, and attach any display logic to match
        // the borrow-from rules (if any)
        var expandedViews = []
        variables.forEach(function(variable) {
          // ignore borrowed loop variables that are "excluded"
          if (lender) {
            if (viewGroup.loopLender.exclusionIds.includes(variable.id)) {
              return
            }
          }

          var label = variable.option || variable.statement
          var clonedViews = []
          var clonedIdByOriginalId = {} /* [originalViewId]: clonedViewId */
          views.forEach(function(originalView) {
            var view
            var value
            var originalViewValueID
            if (originalView.type === Views.QUESTION) {
              originalViewValueID = originalView.question.id
              var questionRaw = _.find(self.survey.questions, {
                id: originalView.question.id,
              })

              // clone the question data using simple JSON stringify & parse
              // as an alternative of using _.cloneDeep() to avoid performance issue
              questionRaw = JSON.parse(JSON.stringify(questionRaw))

              var responderQuestion = self.createResponderQuestion(
                questionRaw,
                originalView.question.number
              )
              view = self.createQuestionView(responderQuestion)
              value = view.question
            }
            if (originalView.type === Views.SECTION) {
              originalViewValueID = originalView.section.id
              var data = _.find(self.survey.data.views, {
                value: { id: originalView.section.id },
              })
              view = self.createSectionView(data.value)
              view.section.number = originalView.section.number
              value = view.section
            }

            // give it a unique id and store the loop info
            value.loopCanonicalId = value.id
            value.loopKey = variable.id
            value.id = glUtils.uuid()

            // map the new cloned view id to it's original view id so
            // that it can be used to refresh logic ids
            clonedIdByOriginalId[originalViewValueID] = value.id

            // map the new id to it's view so that it can be referenced
            if (originalView.type === Views.QUESTION) {
              self.viewsByQuestionId[value.id] = view
            }
            if (originalView.type === Views.SECTION) {
              self.viewsBySectionId[value.id] = view
            }

            // inject loop variables
            value.title = inject(value.title, label)
            value.titlePipes.set(value.title)
            if (value.description) {
              value.description = inject(value.description, label)
              value.descriptionPipes.set(value.description)
            }
            _.each(value.choices, function(choice) {
              choice.label = inject(choice.label, label)
              choice.option = inject(choice.option, label)
            })
            _.each(value.statements, function(statement) {
              statement.label = inject(statement.label, label)
              statement.statement = inject(statement.statement, label)
            })

            // inject loop variable images
            if (viewGroup.loopImagesEnabled && value.imageFromLoop) {
              value.imageUrl = variable.data.imageUrl || value.imageUrl // fallback to original
            }

            clonedViews.push(view)
          })

          // loop through each cloned views to refresh existing logic IDs and lender IDs
          // with cloned IDs, and attach a display logic (if there's none)
          // or attach a logic condition to existing display logic
          // We need to do a separate foreach loop here because we need to
          // map all cloned view ids to original view ids so that it can used
          // to refresh existing logic ids without missing any.
          clonedViews.forEach(function(view) {
            var value
            if (view.type === Views.QUESTION) {
              value = view.question

              // refresh lender ID if exist
              if (value.choiceLender.id) {
                var oldId = value.choiceLender.id
                value.choiceLender.id = clonedIdByOriginalId[oldId] || oldId
              }
              if (value.statementLender.id) {
                var oldId = value.statementLender.id
                value.statementLender.id = clonedIdByOriginalId[oldId] || oldId
              }
            }
            if (view.type === Views.SECTION) {
              value = view.section
            }

            // refresh existing logic IDs with cloned view IDs
            _.each(value.logic, function(logic) {
              logic.refresh(function(oldId) {
                return clonedIdByOriginalId[oldId] || oldId
              })
            })

            // for borrowed variables, attach logic to hide based on borrow settings
            // Currently a view is made visible if only one of the display logic
            // has conditions met. That means we can't add a new display logic
            // to a view that has existing display logic because it will always
            // become visible when the borrow-from rule passes.
            // Instead, the borrow from rules must be applied as an additional
            // logic condition for each existing display logic.
            // If there's no existing display logic then add one.
            if (
              isVariableIdBorrowing[variable.id] &&
              viewGroup.loopLender.reducer
            ) {
              // check if display logic already exists
              var hasDisplayLogic = _.some(value.logic, function(logic) {
                return logic.targetsQuestion() || logic.targetsSection()
              })

              if (!hasDisplayLogic) {
                // if there's no existing display logic, then add one
                var logic = new Logic()
                logic.type = logic.Types.DISPLAY
                if (view.type === Views.QUESTION) {
                  logic.target = logic.Targets.QUESTION
                }
                if (view.type === Views.SECTION) {
                  logic.target = logic.Targets.SECTION
                }
                value.logic.push(logic)
              }

              // attach logic condition matching the borrow-from rules to each display logic
              _.each(value.logic, function(logic) {
                if (!logic.targetsQuestion() && !logic.targetsSection()) {
                  return // continue
                }

                var condition = new LogicCondition(logic)
                condition.questionId = viewGroup.loopLender.id
                condition.loopKey = viewGroup.loopLender.loopKey

                if (
                  [
                    Lender.Reducer.SELECTED,
                    Lender.Reducer.NOT_SELECTED,
                  ].includes(viewGroup.loopLender.reducer)
                ) {
                  condition.type = condition.Types.SELECTED
                  condition.selectionQuantifier =
                    viewGroup.loopLender.reducer === Lender.Reducer.SELECTED
                      ? condition.SelectionQuantifiers.ANY
                      : condition.SelectionQuantifiers.NONE
                  if (
                    viewGroup.loopLender.target === Lender.Target.STATEMENTS
                  ) {
                    condition.statementIds = [variable.id]
                    condition.choiceIds = viewGroup.loopLender.choiceIds.slice()
                  }
                  if (viewGroup.loopLender.target === Lender.Target.CHOICES) {
                    condition.choiceIds = [variable.id]
                  }
                }
                if (
                  [
                    Lender.Reducer.DISPLAYED,
                    Lender.Reducer.NOT_DISPLAYED,
                  ].includes(viewGroup.loopLender.reducer)
                ) {
                  condition.type = condition.Types.DISPLAYED
                  condition.displayedQuantifier =
                    viewGroup.loopLender.reducer === Lender.Reducer.DISPLAYED
                      ? condition.DisplayedQuantifiers.ANY
                      : condition.DisplayedQuantifiers.NONE
                  if (
                    viewGroup.loopLender.target === Lender.Target.STATEMENTS
                  ) {
                    condition.displayedTarget =
                      condition.DisplayedTargets.STATEMENTS
                    condition.statementIds = [variable.id]
                  }
                  if (viewGroup.loopLender.target === Lender.Target.CHOICES) {
                    condition.displayedTarget =
                      condition.DisplayedTargets.CHOICES
                    condition.choiceIds = [variable.id]
                  }
                }
                logic.conditions.push(condition)
              })
            }
            expandedViews.push(view)
          })
        })

        // [startIdx, 0, ...expandedViews]
        var args = expandedViews.slice()
        args.unshift(0)
        args.unshift(startIdx)
        self.views.splice.apply(self.views, args)
      })
    }

    Responder.prototype.getViewId = function(view) {
      var value
      if (view.type === Views.SECTION) value = view.section
      if (view.type === Views.QUESTION) value = view.question
      if (!value) return null
      return value.loopKey ? value.loopCanonicalId : value.id
    }

    Responder.prototype.getViewById = function(viewId) {
      return this.views.find(function(view) {
        var id = null
        if (view.type === Views.SECTION) id = view.section.id
        if (view.type === Views.QUESTION) id = view.question.id
        return id === viewId
      })
    }

    Responder.prototype.getViewByLoopCanonicalIdAndLoopKey = function(
      loopCanonicalId,
      loopKey
    ) {
      return this.views.find(function(view) {
        var id, key
        if (view.type === Views.SECTION) {
          id = view.section.loopCanonicalId
          key = view.section.loopKey
        }
        if (view.type === Views.QUESTION) {
          id = view.question.loopCanonicalId
          key = view.question.loopKey
        }
        return loopCanonicalId === id && loopKey === key
      })
    }

    /**
     * Get View Progress
     * @desc calculate the progress percentage for a view
     * @param {Object} view - The view to calculate progress for
     * @returns {Number} progress - The views progress
     */
    Responder.prototype.getViewProgress = function(view) {
      var self = this

      // calculate view progress
      var views = _.filter(self.views, isCounted)
      var viewPos = _.indexOf(views, view) + 1
      return (viewPos / views.length) * 100

      // determine if a view is counted in progression
      function isCounted(view) {
        var isCorrectViewType = _.includes(
          [Views.QUESTION, Views.SECTION, Views.PAGE],
          view.type
        )
        var isAutocompleted = false

        switch (view.type) {
          case Views.QUESTION:
            isAutocompleted = !!view.question.autocompletedByIdentity
            break
          case Views.PAGE:
            _.each(view.questions, function(question) {
              // Iteration will exit if isAutocompleted is false, thus view is counted
              isAutocompleted = !!question.autocompletedByIdentity
              return isAutocompleted
            })
            break
        }

        return isCorrectViewType && !isAutocompleted
      }
    }

    /**
     * Get Dimensions
     * @desc get all dimensions optionally filtered by type
     * @param {String} type - The type to filter by (optional)
     * @returns {Array} dimensions
     */
    Responder.prototype.getDimensions = function(type) {
      var self = this
      var filter = type ? { type: type } : undefined
      return _.filter(self.dimensions, filter)
    }

    Responder.prototype.getDimension = function(type, key) {
      return _.find(this.dimensions, { type: type, key: key })
    }

    /**
     * Set Dimension
     * @desc add or update a dimension
     * @param {String} type - The type of dimension
     * @param {String} key - The dimension key
     * @param {String} value - The dimension value
     * @returns {Object} dimension - The created/updated dimension
     */
    Responder.prototype.setDimension = function(type, key, value) {
      var self = this
      if (!type || !key) {
        return console.error('Response.setDimension requires a type and key')
      }
      var dimension = _.find(self.dimensions, { type: type, key: key })
      if (!dimension && !value) return
      if (dimension) {
        if (value) {
          dimension.value = value
        } else {
          _.remove(self.dimensions, dimension)
        }
        return dimension
      }

      dimension = {
        type: type,
        key: key,
        value: value,
      }
      self.dimensions.push(dimension)
      return dimension
    }

    Responder.prototype.setDelay = function(seconds, onDone) {
      var self = this

      // clear any pre-existing delay
      $timeout.cancel(self.delayed)
      self.delayed = null

      // start a timer if valid delay provided
      if (seconds) {
        self.delayed = $timeout(function() {
          self.delayed = null
          onDone()
        }, seconds * 1000)
      }
    }

    Responder.prototype.setHeaders = function(headers) {
      var self = this
      _.each(headers, function(value, key) {
        self.headers[key] = value
      })
    }

    // We need to pass viewLoopKey argument because there are 2 states of view
    // when validating logic conditions:
    // - CURRENT view - To trigger logic actions & validate choices/statements display logic
    // - NEXT view - To validate display logic and is validated before the view is shown.
    // We could know what is the CURRENT view, but we couldn't know what is the
    // NEXT view until the view is shown and become the current view
    // so the easiest approach is to pass down the view.loopKey
    // value when invoking this method, but it does make the codes more complex.
    Responder.prototype.allConditionsMet = function(conditions, viewLoopKey) {
      var self = this

      function getQuestion(condition, allowUnseen) {
        var view = condition.loopKey
          ? self.getViewByLoopCanonicalIdAndLoopKey(
              condition.questionId,
              condition.loopKey
            )
          : self.getViewById(condition.questionId)
        if (!view || (!view.question.isSeen && !allowUnseen)) {
          // return null if no view exists OR (if the view is not seen AND unseen views are not allowed)
          return null
        }
        return view.question
      }

      return _.every(conditions, function(condition) {
        if (condition.type === LogicCondition.Types.SELECTED) {
          var question = getQuestion(condition)
          if (!question) return false // hard stop!

          var choiceIds = _.filter(condition.choiceIds, function(choiceId) {
            var choice = _.find(question.choices, { id: choiceId })
            return choice && !choice.isHidden
          })
          var statementIds = _.filter(condition.statementIds, function(
            statementId
          ) {
            var statement = _.find(question.statements, { id: statementId })
            return statement && !statement.isHidden
          })

          // constant sum
          // currently only constant sum uses choiceQuantifier combined with numericQuantifier
          if (question.type === question.Types.CONSTANT_SUM) {
            var selected = question.selected
            if (statementIds.length) {
              selected = _.chain(statementIds)
                .map(function(statementId) {
                  return question.statementAnswers[statementId]
                })
                .flatten()
                .uniqBy(function(choice) {
                  return choice.id
                })
                .value()
            }
            var choices = _.filter(selected, function(choice) {
              return _.includes(choiceIds, choice.id)
            })
            var lodashMethod
            if (
              condition.choiceQuantifier ===
              LogicCondition.ChoiceQuantifiers.ANY
            ) {
              lodashMethod = 'some'
            }
            if (
              condition.choiceQuantifier ===
              LogicCondition.ChoiceQuantifiers.ALL
            ) {
              lodashMethod = 'every'
            }
            if (
              condition.numericQuantifier ===
              LogicCondition.NumericQuantifiers.LESS_THAN
            ) {
              return _[lodashMethod](choices, function(choice) {
                return choice.numberValue < condition.number
              })
            }
            if (
              condition.numericQuantifier ===
              LogicCondition.NumericQuantifiers.GREATER_THAN
            ) {
              return _[lodashMethod](choices, function(choice) {
                return choice.numberValue > condition.number
              })
            }
            if (
              condition.numericQuantifier ===
              LogicCondition.NumericQuantifiers.EQUALS
            ) {
              return _[lodashMethod](choices, function(choice) {
                return choice.numberValue === condition.number
              })
            }
            if (
              condition.numericQuantifier ===
              LogicCondition.NumericQuantifiers.ANY
            ) {
              return _[lodashMethod](choices, function(choice) {
                return _.includes(condition.numbers, choice.numberValue)
              })
            }
            if (
              condition.numericQuantifier ===
              LogicCondition.NumericQuantifiers.NONE
            ) {
              return _[lodashMethod](choices, function(choice) {
                return !_.includes(condition.numbers, choice.numberValue)
              })
            }
          }

          // text
          var usesText =
            question.type === question.Types.LOCATION ||
            question.type === question.Types.TEXTAREA ||
            (question.type === question.Types.TEXT &&
              _.includes([null, 'EMAIL'], question.textValidator))

          if (usesText) {
            var questionText
            if (question.type === question.Types.TEXT) {
              questionText = question.getValidTexts().join(' ')
            } else {
              questionText = question.text
            }

            if (!questionText) return false
            var texts = condition.text.split(',').map(function(text) {
              return new RegExp(text.trim(), 'i')
            })
            if (
              condition.textQuantifier === LogicCondition.TextQuantifiers.ALL
            ) {
              return _.every(texts, function(textRegex) {
                return textRegex.test(questionText)
              })
            }
            if (
              condition.textQuantifier === LogicCondition.TextQuantifiers.ANY
            ) {
              return _.some(texts, function(textRegex) {
                return textRegex.test(questionText)
              })
            }
            if (
              condition.textQuantifier === LogicCondition.TextQuantifiers.NONE
            ) {
              return !_.some(texts, function(textRegex) {
                return textRegex.test(questionText)
              })
            }
          }

          // selected for normal questions VS per statement on matrix
          var selected = question.selected
          if (statementIds.length) {
            selected = _.chain(statementIds)
              .map(function(statementId) {
                return question.statementAnswers[statementId]
              })
              .flatten()
              .uniqBy(function(choice) {
                return choice.id
              })
              .value()
          }

          // rank
          if (question.type === question.Types.RANK) {
            function hasAnyRank(choice) {
              return condition.ranks.includes(choice.rank)
            }
            if (
              condition.selectionQuantifier ===
              LogicCondition.SelectionQuantifiers.ALL
            ) {
              return _.every(choiceIds, function(choiceId) {
                return !!_.find(selected, function(choice) {
                  return choice.id === choiceId && hasAnyRank(choice)
                })
              })
            }
            if (
              condition.selectionQuantifier ===
              LogicCondition.SelectionQuantifiers.ANY
            ) {
              return _.some(selected, function(choice) {
                return _.includes(choiceIds, choice.id) && hasAnyRank(choice)
              })
            }
            if (
              condition.selectionQuantifier ===
              LogicCondition.SelectionQuantifiers.NONE
            ) {
              return !_.some(selected, function(choice) {
                return _.includes(choiceIds, choice.id) && hasAnyRank(choice)
              })
            }
            if (
              condition.selectionQuantifier ===
              LogicCondition.SelectionQuantifiers.EXACTLY
            ) {
              return (
                selected.length === choiceIds.length &&
                _.every(selected, function(choice) {
                  return !!_.find(choiceIds, function(choiceId) {
                    return choice.id === choiceId && hasAnyRank(choice)
                  })
                })
              )
            }
          }

          // choices
          var usesChoices = _.includes(
            [
              question.Types.CHOICE,
              question.Types.NPS,
              question.Types.MOOD,
              question.Types.RATING,
              question.Types.MATRIX,
              question.Types.SCALE,
              question.Types.SCORE,
            ],
            question.type
          )
          if (usesChoices) {
            if (
              condition.selectionQuantifier ===
              LogicCondition.SelectionQuantifiers.ALL
            ) {
              return _.every(choiceIds, function(choiceId) {
                return !!_.find(selected, function(choice) {
                  return choice.id === choiceId
                })
              })
            }
            if (
              condition.selectionQuantifier ===
              LogicCondition.SelectionQuantifiers.ANY
            ) {
              return _.some(selected, function(choice) {
                return _.includes(choiceIds, choice.id)
              })
            }
            if (
              condition.selectionQuantifier ===
              LogicCondition.SelectionQuantifiers.NONE
            ) {
              return !_.some(selected, function(choice) {
                return _.includes(choiceIds, choice.id)
              })
            }
            if (
              condition.selectionQuantifier ===
              LogicCondition.SelectionQuantifiers.EXACTLY
            ) {
              return (
                selected.length === choiceIds.length &&
                _.every(selected, function(choice) {
                  return !!_.find(choiceIds, function(choiceId) {
                    return choice.id === choiceId
                  })
                })
              )
            }
          }

          // numbers
          var usesNumbers =
            question.type === question.Types.NUMERIC ||
            (question.type === question.Types.TEXT &&
              _.includes(['NUMERIC', 'NUMERIC_RANGE'], question.textValidator))
          if (usesNumbers) {
            var numbers
            if (question.type === question.Types.NUMERIC) {
              var number = question.selected[0]
                ? question.selected[0].number
                : null
              numbers = [number]
            } else if (question.type === question.Types.TEXT) {
              numbers = question.getValidTexts().map(function(text) {
                return +text
              })
            } else {
              numbers = [+question.text]
            }

            // The only reason there can be more than 1 number here is when a text
            // question has more than 1 field.
            // In this case, the condition only needs to apply to one for the condition
            // to be met.
            // In all other cases, "some" actually means exactly 1 in this context.
            if (
              condition.numericQuantifier ===
              LogicCondition.NumericQuantifiers.LESS_THAN
            ) {
              return _.some(numbers, function(number) {
                return number < condition.number
              })
            }
            if (
              condition.numericQuantifier ===
              LogicCondition.NumericQuantifiers.GREATER_THAN
            ) {
              return _.some(numbers, function(number) {
                return number > condition.number
              })
            }
            if (
              condition.numericQuantifier ===
              LogicCondition.NumericQuantifiers.EQUALS
            ) {
              return _.some(numbers, function(number) {
                return number === condition.number
              })
            }
            if (
              condition.numericQuantifier ===
              LogicCondition.NumericQuantifiers.ANY
            ) {
              return _.some(condition.numbers, function(n) {
                return _.includes(numbers, n)
              })
            }
            if (
              condition.numericQuantifier ===
              LogicCondition.NumericQuantifiers.NONE
            ) {
              return !_.some(condition.numbers, function(n) {
                return _.includes(numbers, n)
              })
            }
          }
        }
        if (condition.type === LogicCondition.Types.ANSWER_COUNT) {
          var question = getQuestion(condition)
          if (!question) return false // hard stop!

          var statementIds = _.filter(condition.statementIds, function(
            statementId
          ) {
            var statement = _.find(question.statements, { id: statementId })
            return statement && !statement.isHidden
          })

          // text
          var usesText = question.type === question.Types.TEXT
          // choices
          var usesChoices = _.includes(
            [
              question.Types.CHOICE,
              question.Types.RANK,
              question.Types.MATRIX,
              question.Types.CONSTANT_SUM,
            ],
            question.type
          )

          var amount = 0
          if (usesText) {
            amount = question.getValidTexts().length
          }
          if (usesChoices) {
            var selected = question.selected
            if (statementIds.length) {
              selected = _.chain(statementIds)
                .map(function(statementId) {
                  return question.statementAnswers[statementId]
                })
                .flatten()
                .uniqBy(function(choice) {
                  return choice.id
                })
                .value()
            }
            amount = selected.length
          }

          if (
            condition.numericQuantifier ===
            LogicCondition.NumericQuantifiers.LESS_THAN
          ) {
            return amount < condition.number
          }
          if (
            condition.numericQuantifier ===
            LogicCondition.NumericQuantifiers.GREATER_THAN
          ) {
            return amount > condition.number
          }
          if (
            condition.numericQuantifier ===
            LogicCondition.NumericQuantifiers.EQUALS
          ) {
            return amount === condition.number
          }
          if (
            condition.numericQuantifier ===
            LogicCondition.NumericQuantifiers.ANY
          ) {
            return _.includes(condition.numbers, amount)
          }
          if (
            condition.numericQuantifier ===
            LogicCondition.NumericQuantifiers.NONE
          ) {
            return !_.includes(condition.numbers, amount)
          }
        }
        if (condition.type === LogicCondition.Types.ANSWERED) {
          var question = getQuestion(condition)
          if (!question) return false // hard stop!
          return question.isAnswered()
        }
        if (condition.type === LogicCondition.Types.DIDNT_ANSWER) {
          // For this condition type, questions are allowed to be not visible (unseen).
          var question = getQuestion(condition, true)
          if (!question) return false // hard stop!
          return !question.isAnswered()
        }
        if (condition.type === LogicCondition.Types.IS_TAGGED) {
          if (
            condition.selectionQuantifier ===
            LogicCondition.SelectionQuantifiers.ALL
          ) {
            return _.every(condition.tags, function(tag) {
              return self.hasTag(tag)
            })
          }
          if (
            condition.selectionQuantifier ===
            LogicCondition.SelectionQuantifiers.ANY
          ) {
            return _.some(condition.tags, function(tag) {
              return self.hasTag(tag)
            })
          }
          if (
            condition.selectionQuantifier ===
            LogicCondition.SelectionQuantifiers.NONE
          ) {
            return _.every(condition.tags, function(tag) {
              return !self.hasTag(tag)
            })
          }
          if (
            condition.selectionQuantifier ===
            LogicCondition.SelectionQuantifiers.EXACTLY
          ) {
            var tags = self.getTags()
            return (
              tags.length === condition.tags.length &&
              _.every(tags, function(appliedTag) {
                return _.includes(condition.tags, appliedTag)
              })
            )
          }
        }
        if (condition.type === LogicCondition.Types.IS_PRIOR_TAGGED) {
          if (
            condition.selectionQuantifier ===
            LogicCondition.SelectionQuantifiers.ALL
          ) {
            return _.every(condition.tags, function(tag) {
              return self.hasPriorTag(tag, condition.period)
            })
          }
          if (
            condition.selectionQuantifier ===
            LogicCondition.SelectionQuantifiers.ANY
          ) {
            return _.some(condition.tags, function(tag) {
              return self.hasPriorTag(tag, condition.period)
            })
          }
          if (
            condition.selectionQuantifier ===
            LogicCondition.SelectionQuantifiers.NONE
          ) {
            return !_.some(condition.tags, function(tag) {
              return self.hasPriorTag(tag, condition.period)
            })
          }
          if (
            condition.selectionQuantifier ===
            LogicCondition.SelectionQuantifiers.EXACTLY
          ) {
            return _.every(condition.tags, function(tag) {
              return self.hasPriorTag(tag, condition.period)
            })
          }
        }
        if (condition.type === LogicCondition.Types.LIST) {
          var items = _.filter(self.listEntries, function(entry) {
            return entry.list === condition.list
          }).map(function(entry) {
            return entry.item
          })
          if (condition.listQuantifier === LogicCondition.ListQuantifiers.ALL) {
            return _.every(condition.listItems, function(item) {
              return _.includes(items, item)
            })
          }
          if (condition.listQuantifier === LogicCondition.ListQuantifiers.ANY) {
            return _.some(condition.listItems, function(item) {
              return _.includes(items, item)
            })
          }
          if (
            condition.listQuantifier === LogicCondition.ListQuantifiers.NONE
          ) {
            return _.every(condition.listItems, function(item) {
              return !_.includes(items, item)
            })
          }
          if (
            condition.listQuantifier ===
            LogicCondition.ListQuantifiers.LESS_THAN
          ) {
            return items.length < condition.number
          }
          if (
            condition.listQuantifier ===
            LogicCondition.ListQuantifiers.GREATER_THAN
          ) {
            return items.length > condition.number
          }
          if (
            condition.listQuantifier === LogicCondition.ListQuantifiers.EQUALS
          ) {
            return items.length === condition.number
          }
        }
        if (condition.type === LogicCondition.Types.DISPLAYED) {
          // For this condition type, questions are allowed to be not visible (unseen).
          var question = getQuestion(condition, true)
          if (!question) return false // hard stop!

          var visibleOptions = []
          var optionIds = []
          if (
            condition.displayedTarget ===
            LogicCondition.DisplayedTargets.CHOICES
          ) {
            visibleOptions = question.getVisibleChoices()
            optionIds = condition.choiceIds
          }
          if (
            condition.displayedTarget ===
            LogicCondition.DisplayedTargets.STATEMENTS
          ) {
            visibleOptions = question.getVisibleStatements()
            optionIds = condition.statementIds
          }
          if (
            condition.displayedQuantifier ===
            LogicCondition.DisplayedQuantifiers.ALL
          ) {
            return _.every(optionIds, function(optionId) {
              return !!_.find(visibleOptions, function(option) {
                return option.id === optionId
              })
            })
          }
          if (
            condition.displayedQuantifier ===
            LogicCondition.DisplayedQuantifiers.NONE
          ) {
            return !_.some(visibleOptions, function(option) {
              return _.includes(optionIds, option.id)
            })
          }
          if (
            condition.displayedQuantifier ===
            LogicCondition.DisplayedQuantifiers.ANY
          ) {
            return _.some(visibleOptions, function(option) {
              return _.includes(optionIds, option.id)
            })
          }
        }
        if (condition.type === LogicCondition.Types.IS_INSIDE_LOOP) {
          if (
            condition.insideLoopQuantifier ===
            LogicCondition.InsideLoopQuantifiers.ANY
          ) {
            return _.includes(condition.loopKeys, viewLoopKey)
          }
          if (
            condition.insideLoopQuantifier ===
            LogicCondition.InsideLoopQuantifiers.NONE
          ) {
            return !_.includes(condition.loopKeys, viewLoopKey)
          }
        }
      })
    }

    Responder.prototype.isViewHiddenByDisplayLogic = function(view) {
      var self = this
      var value
      if (view.question) value = view.question
      if (view.section) value = view.section
      var logic = value.logic
      if (!logic) return false
      var viewDisplayLogic = _.filter(logic, function(logic) {
        return logic.targetsQuestion() || logic.targetsSection()
      })
      if (!viewDisplayLogic.length) return false
      return !_.some(viewDisplayLogic, function(logic) {
        return self.allConditionsMet(logic.conditions, value.loopKey)
      })
    }

    Responder.prototype.isViewHiddenBySampling = function(view) {
      var self = this
      var viewId = self.getViewId(view)
      if (!viewId) return false
      return _.includes(self.hiddenBySamplingViewIds, viewId)
    }

    Responder.prototype.isViewHiddenByHideViewGroupLogic = function(view) {
      var self = this
      return _.some(self.hiddenViewGroupEntries, function(entry) {
        var viewGroup = self.viewGroupsById[entry.viewGroupId]
        var viewId = self.getViewId(view)
        if (!viewId) return false
        return _.includes(viewGroup.viewIds, viewId)
      })
    }

    Responder.prototype.findTriggeredActionLogic = function(logic, loopKey) {
      var self = this
      var actions = []
      _.each(logic, function(logic) {
        if (logic.type !== logic.Types.ACTION) return
        // check all conditions are met and if they are we will
        // add the actions to be performed and returned
        if (self.allConditionsMet(logic.conditions, loopKey)) {
          _.each(logic.actions, function(action) {
            actions.push(action)
          })
        }
      })
      return actions
    }

    Responder.prototype.getCurrentViewIndex = function() {
      return _.last(this.history)
    }

    Responder.prototype.resolveNext = function() {
      var self = this
      var currentViewIndex = _.last(self.history)
      var variablesToApply = []
      var tagOps = []
      var listEntriesToApply = []
      var hiddenViewGroupIdsToApply = []

      if (!_.isNumber(currentViewIndex)) {
        return self.views[0]
      }

      var jumpView
      var canContinue
      var currentView = self.views[currentViewIndex]
      currentView.willExit = false // action logic can make this exit
      currentView.willHitQuota = false // tag quotas can hit quota
      currentView.willQualityExit = false // action logic can make this quality exit

      // remove any tags this view may have given the respondent
      // when they come BACK to this view
      _.remove(self.tagOps, { givenByViewIndex: currentViewIndex })

      // remove any list entries this view may have given the respondent
      // when they come BACK to this view
      _.remove(self.listEntries, { givenByViewIndex: currentViewIndex })

      // remove any variable entries this view may have given the respondent
      // when they come BACK to this view
      _.remove(self.variableEntries, { givenByViewIndex: currentViewIndex })

      // remove any hidden view groups this view may applied
      // when they come BACK to this view
      _.remove(self.hiddenViewGroupEntries, {
        givenByViewIndex: currentViewIndex,
      })

      switch (currentView.type) {
        case Views.INTRO:
        case Views.OUTRO:
          // do nothing
          break
        case Views.SECTION:
          var video = currentView.section.video
          if (video.restrictions.complete && !video.completed) return
          jumpView = findAndProcessActions(
            currentView.section.logic,
            currentView.section.loopKey
          )
          break
        case Views.PAGE:
          canContinue = true
          _.each(currentView.questions, function(question) {
            if (!question.canContinue()) {
              canContinue = false
              return false
            }
          })
          if (!canContinue) return
          break
        case Views.QUESTION:
          canContinue = currentView.question.canContinue()
          if (!canContinue) return
          jumpView = findAndProcessActions(
            currentView.question.logic,
            currentView.question.loopKey
          )
      }

      return nextVisibleView(jumpView)

      function findAndProcessActions(logic, loopKey) {
        var actions = self.findTriggeredActionLogic(logic, loopKey)
        var jumpViews = []
        _.each(actions, function(action) {
          switch (action.type) {
            case LogicAction.Types.EXIT:
              currentView.willExit = true
              jumpViews.push(_.last(self.views))
              break
            case LogicAction.Types.QUALITYEXIT:
              currentView.willQualityExit = true
              jumpViews.push(_.last(self.views))
              break
            case LogicAction.Types.SUBMIT:
              jumpViews.push(_.last(self.views))
              break
            // DEPRECATED
            case LogicAction.Types.JUMP_TO_FOLLOWUP:
              jumpViews.push(
                _.find(self.views, function(view) {
                  return (
                    view.question &&
                    view.question.type === QuestionTypes.FOLLOWUP
                  )
                })
              )
              break
            // DEPRECATED
            case LogicAction.Types.JUMP_TO_PROFILING:
              jumpViews.push(
                _.find(self.views, function(view) {
                  // PAGE is only used for profiling currently
                  return view.type === 'PAGE'
                })
              )
              break
            case LogicAction.Types.JUMP_TO_QUESTION:
              var view = action.loopKey
                ? self.getViewByLoopCanonicalIdAndLoopKey(
                    action.questionId,
                    action.loopKey
                  )
                : self.getViewById(action.questionId)
              jumpViews.push(view)
              break
            case LogicAction.Types.JUMP_TO_SECTION:
              var view = action.loopKey
                ? self.getViewByLoopCanonicalIdAndLoopKey(
                    action.sectionId,
                    action.loopKey
                  )
                : self.getViewById(action.sectionId)
              jumpViews.push(view)
              break
            case LogicAction.Types.TAG:
              tagOps.push({
                type: 'ADD',
                tag: action.tag,
                givenByViewIndex: currentViewIndex,
              })
              break
            case LogicAction.Types.UNTAG:
              action.tags.forEach(function(tag) {
                tagOps.push({
                  type: 'REMOVE',
                  tag: tag,
                  givenByViewIndex: currentViewIndex,
                })
              })
              break
            case LogicAction.Types.ADD_TO_LIST:
              listEntriesToApply.push({
                list: action.list,
                item: action.listItem,
              })
              break
            case LogicAction.Types.HIDE_VIEW_GROUPS:
              _.each(action.viewGroupIds, function(viewGroupId) {
                hiddenViewGroupIdsToApply.push(viewGroupId)
              })
              break
            case LogicAction.Types.ADD_LEAST_FILLED_TAGS:
              action.leastFill
                .getTagsToAdd(self.tagCounts)
                .forEach(function(tag) {
                  tagOps.push({
                    type: 'ADD',
                    tag: tag,
                    givenByViewIndex: currentViewIndex,
                  })
                })
              break
            case LogicAction.Types.RETAIN_LEAST_FILLED_TAGS:
              var allTags = self.resolveTags(self.tagOps.concat(tagOps))
              action.leastFill
                .getTagsToRemove(self.tagCounts, allTags)
                .forEach(function(tag) {
                  tagOps.push({
                    type: 'REMOVE',
                    tag: tag,
                    givenByViewIndex: currentViewIndex,
                  })
                })
              break
            case LogicAction.Types.SET_VARIABLE:
              variablesToApply.push({
                key: action.variableKey,
                value: action.variableValue,
              })
              break
          }
        })

        self.applyHiddenViewGroupIds(
          hiddenViewGroupIdsToApply,
          currentViewIndex
        )
        self.applyListEntries(listEntriesToApply, currentViewIndex)
        self.applyVariables(variablesToApply, currentViewIndex)

        var isOverQuota = self.applyTags(tagOps)
        if (isOverQuota) {
          currentView.willHitQuota = true
          return _.last(self.views)
        }

        // Rarely logic might resolve to multiple jumps. In this case
        // we only honour the first jump
        return _.find(jumpViews, function(jumpView) {
          return _.isPlainObject(jumpView)
        })
      }

      function nextVisibleView(jumpView) {
        var startViewIndex = jumpView ? jumpView.index - 1 : currentViewIndex
        return _.find(self.views, function(view, index) {
          if (index <= startViewIndex) {
            return
          }

          if (self.isViewHiddenByHideViewGroupLogic(view)) return false

          switch (view.type) {
            case Views.QUESTION:
              return (
                !view.question.autocompletedByIdentity &&
                !self.isViewHiddenByDisplayLogic(view) &&
                !self.isViewHiddenBySampling(view) &&
                !view.question.isHiddenByInsufficientOptions()
              )
            case Views.SECTION:
              return (
                !self.isViewHiddenByDisplayLogic(view) &&
                !self.isViewHiddenBySampling(view)
              )
            case Views.PAGE:
              var allAutocompleted = true
              _.each(view.questions, function(question) {
                if (!question.autocompletedByIdentity) {
                  allAutocompleted = false
                  return false
                }
              })
              return !allAutocompleted
          }

          return true
        })
      }
    }

    Responder.prototype.next = function(view) {
      var self = this
      var currentView = self.getView()
      var currentViewIndex = _.last(self.history)

      if (currentView && currentView.type === Views.QUESTION) {
        currentView.question.runPostValidation(currentViewIndex)
      }

      // A view can be passed into this method for "jump-to" functionality
      // when previewing. This shouldn't be used for live responding surveys
      // since it will break the submission rules.
      if (!view) {
        view = self.resolveNext()
      }

      if (currentView && currentView.type === Views.OUTRO) {
        return self.dispatch(Events.DONE)
      }
      if (!view) {
        return console.error('there is no next view')
      }

      if (currentView && currentView.willExit) {
        self.didExit = true
      }
      if (currentView && currentView.willHitQuota) {
        self.didHitQuota = true
      }
      if (currentView && currentView.willQualityExit) {
        self.didQualityExit = true
      }

      var nextViewIndex = _.indexOf(self.views, view)

      // mark in-between questions as unseen.
      // remove any tags that may have been applied when
      // respondent DID see this question
      _.each(self.views, function(view, index) {
        if (index > currentViewIndex && index < nextViewIndex) {
          _.remove(self.tagOps, { givenByViewIndex: index })
          switch (view.type) {
            case Views.QUESTION:
              view.question.isSeen = false
              break
            case Views.PAGE:
              _.each(view.questions, function(question) {
                question.isSeen = false
              })
              break
          }
        }
      })

      switch (view.type) {
        case Views.QUESTION:
          view.question.isSeen = true
          break
        case Views.PAGE:
          _.each(view.questions, function(question) {
            question.isSeen = true
          })
          break
      }

      self.history.push(nextViewIndex)
      self.actionClicked = 'next'
      self.dispatch(Events.VIEW_CHANGED, view)
      self.trackViewCompleted(currentView)

      // fire "start" event after the first user-actioned progress
      var userDidStart = currentView && !self.hasStarted
      if (userDidStart) {
        self.dispatch(Events.START, self)
        self.hasStarted = true
      }
    }

    Responder.prototype.trackViewCompleted = function(view) {
      var self = this

      if (self.isReadOnly) {
        return
      }

      if (!view || view.didTrackCompletion) {
        return
      }

      if (view.type === Views.INTRO) {
        self.dispatch(Events.VIEW_COMPLETED, {
          action: 'clicked-begin',
          label: 'survey: ' + self.survey.id,
        })
      }

      if (view.type === Views.QUESTION) {
        var label = [
          'survey: ' + self.survey.id,
          'question: ' +
            glUtils.pad(view.question.number, self.totalQuestions) +
            '/' +
            self.totalQuestions,
        ].join(' ')
        self.dispatch(Events.VIEW_COMPLETED, {
          questionId: view.question.id,
          action: 'answered-question',
          label: label,
        })

        if (view.question.type === QuestionTypes.FOLLOWUP) {
          var isRequested = !!view.question.selected.length
          var action = isRequested ? 'requested-followup' : 'declined-followup'
          label = 'survey: ' + self.survey.id
          self.dispatch(Events.VIEW_COMPLETED, {
            action: action,
            label: label,
          })
        }
      }

      if (view.type === Views.PAGE) {
        _.each(view.questions, function(question) {
          var label = [
            'survey: ' + self.survey.id,
            'question: ' +
              glUtils.pad(question.number, self.totalQuestions) +
              '/' +
              self.totalQuestions,
          ].join(' ')
          self.dispatch(Events.VIEW_COMPLETED, {
            questionId: question.id,
            action: 'answered-question',
            label: label,
          })
        })
      }

      view.didTrackCompletion = true
    }

    Responder.prototype.getView = function() {
      var self = this
      var viewIndex = _.last(self.history)
      return self.views[viewIndex]
    }

    Responder.prototype.getQuestionById = function(questionId) {
      var view = _.find(this.views, function(view) {
        return view.type === Views.QUESTION && view.question.id === questionId
      })
      return view && view.question
    }

    Responder.prototype.getQuestionByIdAndLoopKey = function(
      questionId,
      loopKey
    ) {
      var view = this.getViewByLoopCanonicalIdAndLoopKey(questionId, loopKey)
      return view && view.question
    }

    Responder.prototype.resolvePrev = function() {
      var self = this
      var viewIndex = self.history[self.history.length - 2]
      if (!_.isNumber(viewIndex)) {
        return
      }
      return self.views[viewIndex]
    }

    Responder.prototype.prev = function() {
      var self = this
      var view = self.resolvePrev()
      if (!view) {
        return console.error('there is no previous view')
      }
      switch (view.type) {
        case Views.QUESTION:
          view.question.isSeen = true
          break
        case Views.PAGE:
          _.each(view.questions, function(question) {
            question.isSeen = true
          })
          break
      }
      self.history.pop()
      self.actionClicked = 'back'
      self.dispatch(Events.VIEW_CHANGED, view)
    }

    Responder.prototype.nextAction = function() {
      var self = this
      var currentView = self.getView()
      var nextView = self.resolveNext()
      var action = {}

      if (currentView.type === Views.OUTRO) {
        action.text = self.survey.data.doneButton || self.labels.DONE
        action.isDisabled = !self.isSent
        return action
      }

      if (this.delayed) {
        action.text = self.labels.NEXT
        action.isDisabled = true
        return action
      }

      if (!nextView) {
        action.text = self.labels.NEXT
        action.isDisabled = true
        return action
      }

      if (currentView.type === Views.INTRO) {
        action.text = self.labels.GET_STARTED
        return action
      }

      if (currentView.type === Views.SECTION) {
        action.text = self.labels.NEXT
        return action
      }

      var isAnswered
      if (currentView.type === Views.QUESTION) {
        var canContinue = currentView.question.canContinue()
        isAnswered = currentView.question.isAnswered()

        // Next or Skip?
        if (canContinue && !isAnswered) {
          action.text = self.labels.SKIP
        } else {
          action.text = self.labels.NEXT
        }

        // Disabled or Not Disabled?
        if (!canContinue) {
          action.isDisabled = true
        }

        // Sticky?
        if (isAnswered) {
          action.isSticky = true
        }
      }

      if (currentView.type === Views.PAGE) {
        canContinue = true
        isAnswered = true

        _.each(currentView.questions, function(question) {
          if (!question.canContinue()) {
            canContinue = false
            return false
          }
        })
        _.each(currentView.questions, function(question) {
          if (!question.isAnswered()) {
            isAnswered = false
            return false
          }
        })

        // Next or Skip?
        if (canContinue && !isAnswered) {
          action.text = self.labels.SKIP
        } else {
          action.text = self.labels.NEXT
        }

        // Disabled or Not Disabled?
        if (!canContinue) {
          action.isDisabled = true
        }

        // Sticky?
        if (isAnswered) {
          action.isSticky = true
        }
      }

      if (nextView.type === Views.OUTRO) {
        var willLeave =
          currentView.willExit ||
          currentView.willHitQuota ||
          currentView.willQualityExit
        action.text = willLeave ? self.labels.NEXT : self.labels.SUBMIT
      }

      return action
    }

    Responder.prototype.resolveTags = function(tagOps) {
      var tags = {}
      _.each(tagOps, function(op) {
        if (op.type === 'ADD') {
          tags[op.tag] = true
        } else {
          delete tags[op.tag]
        }
      })
      return _.keys(tags)
    }

    Responder.prototype.hasPriorTag = function(tag, period) {
      return !!_.find(this.priorTags, function(item) {
        return item.tag === tag && item.daysAgo < period
      })
    }

    Responder.prototype.getTags = function() {
      return this.resolveTags(this.tagOps)
    }

    Responder.prototype.applyTags = function(tagOps) {
      var self = this
      var tags = this.resolveTags(tagOps)
      // if any tags applied hit an incomplete tag quota we will continue
      // so that we can fill it - even if they also hit a different complete tag
      // quota at the same time.
      // otherwise if we hit a complete tag quota we will end the survey with
      // over-quota.
      var hitIncompleteTagQuota = _.some(tags, function(tag) {
        return _.includes(self.tagQuotas.incomplete, tag)
      })
      var hitCompleteTagQuota = _.some(tags, function(tag) {
        return _.includes(self.tagQuotas.complete, tag)
      })
      self.tagOps = self.tagOps.concat(tagOps)
      var isOverQuota = hitCompleteTagQuota && !hitIncompleteTagQuota
      return isOverQuota
    }

    Responder.prototype.hasTag = function(tag) {
      var op = _.findLast(this.tagOps, { tag: tag })
      return op && op.type === 'ADD'
    }

    Responder.prototype.applyHiddenViewGroupIds = function(
      viewGroupIds,
      givenByViewIndex
    ) {
      var self = this
      _.each(viewGroupIds, function(viewGroupId) {
        var existing = _.find(self.hiddenViewGroupEntries, function(entry) {
          return entry.viewGroupId === viewGroupId
        })
        if (existing) return
        self.hiddenViewGroupEntries.push({
          viewGroupId: viewGroupId,
          givenByViewIndex: givenByViewIndex,
        })
      })
    }

    Responder.prototype.applyListEntries = function(
      listEntries,
      givenByViewIndex
    ) {
      var self = this
      _.each(listEntries, function(entry) {
        var existing = _.find(self.listEntries, function(e) {
          return e.list === entry.list && e.item === entry.item
        })
        if (existing) return
        self.listEntries.push({
          list: entry.list,
          item: entry.item,
          givenByViewIndex: givenByViewIndex,
        })
      })
    }

    Responder.prototype.applyVariables = function(variables, givenByViewIndex) {
      var self = this
      _.each(variables, function(variable) {
        self.variableEntries.push({
          key: variable.key,
          value: variable.value,
          givenByViewIndex: givenByViewIndex,
        })
      })
    }

    Responder.prototype.dispatch = function(eventName, data) {
      $rootScope.$broadcast(eventName, data)
    }

    /**
     * @name To Server
     * @desc serializes this response into data for the service
     * @returns {Object} data - The data for use with the service
     */
    Responder.prototype.toServer = function() {
      var self = this
      var survey = self.survey
      var data = {}

      data.id = glUtils.uuid() // pre-generated
      data.surveyId = survey.id
      data.respondentId = self.respondentId
      data.glowId = self.deviceId
      data.surveyResponse = {}

      var questionViews = _.filter(self.views, function(view) {
        return view.type === Views.QUESTION && !view.question.submitAsDimension
      })
      questionViews = _.cloneDeep(questionViews)

      // delete keyed loops that weren't answered
      _.remove(questionViews, function(view) {
        var question = view.question
        return question.loopKey && !question.isAnswered()
      })

      data.surveyResponse.responseAnswers = _.map(questionViews, function(
        view
      ) {
        return view.question.toServer()
      })

      // section durations
      var sectionViews = _.filter(self.views, function(view) {
        return view.type === Views.SECTION
      })
      data.sectionDurations = {}
      _.each(sectionViews, function(view) {
        var section = view.section
        var duration = section.timer.getDurationInSeconds(1)
        if (section.loopKey) {
          var sectionOriginalId = section.loopCanonicalId
          if (!data.sectionDurations[sectionOriginalId]) {
            data.sectionDurations[sectionOriginalId] = {}
          }
          data.sectionDurations[sectionOriginalId][section.loopKey] = duration
        } else {
          data.sectionDurations[section.id] = duration
        }
      })

      var pageViews = _.filter(self.views, { type: Views.PAGE })
      _.each(pageViews, function(view) {
        _.each(view.questions, function(question) {
          data.surveyResponse.responseAnswers.push(question.toServer())
        })
      })

      var questionDimensionViews = _.filter(self.views, function(view) {
        return view.type === Views.QUESTION && view.question.submitAsDimension
      })
      _.each(questionDimensionViews, function(view) {
        self.setDimension(
          Dimensions.REPORT,
          view.question.autocompleteKey,
          view.question.getAnswerText()
        )
      })

      data.tags = []
      self.getTags().forEach(function(tag) {
        data.tags.push(tag)
        self.setDimension(Dimensions.REPORT, 'tag:' + tag, 'true')
      })

      // DISABLED: dimensions are single key-value so we can't add list items
      // to the dimension data to show up in filters/exports
      // _.each(self.listEntries, function(entry) {
      //   self.setDimension(Dimensions.REPORT, 'List: ' + entry.list, entry.item)
      // })

      data.customData = {}
      _.each(self.getDimensions(Dimensions.DATA), function(dimension) {
        data.customData[dimension.key] = dimension.value
      })

      data.reportDimensions = {}

      data.reportDimensions.rd = []
      _.each(self.getDimensions(Dimensions.SYSTEM), function(dimension) {
        var p = {}
        p[dimension.key] = dimension.value
        data.reportDimensions.rd.push(p)
      })

      data.reportDimensions.crd = []
      _.each(self.getDimensions(Dimensions.REPORT), function(dimension) {
        var p = {}
        p[dimension.key] = dimension.value
        data.reportDimensions.crd.push(p)
      })

      data.location = self.location

      // Always get the responseId back
      data.reflectId = true

      data.startedAt = this.timer.startedAt.toISOString()
      data.finishedAt = this.timer.stoppedAt.toISOString()
      data.durationSeconds = this.timer.getDurationInSeconds()

      return data
    }

    Responder.prototype.getFollowupDetails = function() {
      var self = this
      var view = _.find(self.views, function(view) {
        return (
          view.type === Views.QUESTION &&
          view.question.type === QuestionTypes.FOLLOWUP &&
          view.question.selected.length
        )
      })
      if (!view || !view.question.email) {
        return
      }
      return {
        email: view.question.email,
        fullname: view.question.fullname,
      }
    }

    Responder.prototype.markFinished = function() {
      // prevent doing this more than once
      if (this.timer.stoppedAt) return
      this.timer.stop()
    }

    Responder.prototype.markSent = function(didSend, options) {
      var self = this
      if (didSend) {
        self.responseId = options && options.responseId
        self.exitId = options && options.exitId
        self.quotaHitId = options && options.quotaHitId
        self.isSent = true
        self.storeAutocompletes()
        self.dispatch(Events.SENT)
      } else {
        self.dispatch(Events.SENT_ERROR)
      }
    }

    Responder.prototype.injectValues = function(pipes, loopKey) {
      var self = this
      function getAnswer(
        questionNumber,
        selectionNumber,
        statementNumber,
        onlyTextEntry,
        onlyNumberEntry,
        onlySumEntry
      ) {
        var view = _.find(self.views, function(view) {
          return (
            view.question &&
            view.question.originalNumber === questionNumber &&
            (view.question.loopKey ? view.question.loopKey === loopKey : true)
          )
        })
        return (
          view &&
          view.question.getAnswerText(
            selectionNumber,
            statementNumber,
            onlyTextEntry,
            onlyNumberEntry,
            onlySumEntry
          )
        )
      }
      function getDimension(type, key) {
        var dimension = self.getDimension(type, key)
        return dimension && dimension.value
      }
      function getParam(key) {
        return $location.search()[key]
      }
      function getList(name, number, html) {
        var listItems = self.listEntries
          .filter(function(entry) {
            return entry.list === name
          })
          .map(function(entry) {
            return entry.item
          })
        if (number) return listItems[number - 1]
        if (html) {
          return listItems
            .map(function(item) {
              return '<li>' + item + '</li>'
            })
            .join('')
        } else {
          return listItems
            .map(function(item) {
              return '- ' + item
            })
            .join('\r\n')
        }
      }
      function getTag(tag) {
        return !!self.hasTag(tag)
      }
      function getVariable(key) {
        var entry = _.findLast(self.variableEntries, { key: key })
        return entry && entry.value
      }
      return pipes.injectValues(
        getAnswer,
        getDimension,
        getParam,
        getList,
        getTag,
        getVariable
      )
    }

    /**
     * Autocomplete
     * @desc autocompletes certain questions based on the storage method
     * @param {UserStorage|TransientStorage} storage - the storage instance to use
     */
    Responder.prototype.autocomplete = function(storage) {
      if (this.isReadOnly || this.isKiosk) {
        return
      }
      this.storage = storage

      _.each(this.questions, function(question) {
        question.autocomplete(storage)
      })

      $rootScope.$broadcast(Responder.Events.AUTOCOMPLETED)
    }

    /**
     * Store Autocompletes
     * @desc notifies autocompleted questions to store their answers based on the current storage method
     */
    Responder.prototype.storeAutocompletes = function() {
      var self = this

      if (!self.storage || self.isReadOnly) {
        return
      }

      _.each(this.questions, function(question) {
        question.storeAutocomplete(self.storage)
      })

      self.storage.save()
    }

    Responder.prototype.getPrevSelectedPlaceIds = function() {
      var previousViews = this.views.slice(0, this.getCurrentViewIndex())
      return _.filter(previousViews, function(view) {
        return (
          view.question &&
          view.question.type === ResponderQuestion.Types.LOCATION &&
          view.question.locationType ===
            ResponderQuestion.LocationTypes.PLACES &&
          !!view.question.text
        )
      }).map(function(view) {
        var slot = locationSetService.getInfoByType(
          locationSetService.Type.PLACE_ID
        ).slot
        return view.question.text.split('|')[slot]
      })
    }

    return Responder
  }
})()
